import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

const GOALS = [
  { id: 'weight-loss', label: 'Lose weight' },
  { id: 'energy', label: 'More energy' },
  { id: 'sleep', label: 'Better sleep' },
  { id: 'recovery', label: 'Faster recovery' },
  { id: 'muscle', label: 'Build or preserve muscle' },
  { id: 'cognitive', label: 'Sharper focus and mental clarity' },
  { id: 'hormones', label: 'Hormone optimization' },
  { id: 'longevity', label: 'Healthy aging and longevity' },
];

const ENERGY_OPTIONS = [
  { id: 'great', label: 'Great — I feel strong and consistent' },
  { id: 'okay', label: 'Okay — some good days, some crashes' },
  { id: 'low', label: 'Low — I feel tired most of the time' },
  { id: 'very-low', label: 'Very low — fatigue is significantly affecting my life' },
];

const SLEEP_OPTIONS = [
  { id: 'great', label: 'Great — I wake up rested' },
  { id: 'okay', label: 'Okay — mostly fine but not always' },
  { id: 'poor', label: 'Poor — I struggle to sleep or stay asleep' },
  { id: 'very-poor', label: 'Very poor — sleep is a major issue for me' },
];

const ACTIVITY_OPTIONS = [
  { id: 'sedentary', label: 'Mostly sedentary' },
  { id: 'light', label: 'Lightly active (walks, some movement)' },
  { id: 'moderate', label: 'Moderately active (exercise 3x per week)' },
  { id: 'very-active', label: 'Very active (exercise 5+ days per week)' },
];

const EXPERIENCE_OPTIONS = [
  { id: 'glp1', label: 'GLP-1 medications (Ozempic, Wegovy, Mounjaro, etc.)' },
  { id: 'peptides', label: 'Peptide therapies' },
  { id: 'hrt', label: 'Hormone therapy (HRT or TRT)' },
  { id: 'nad', label: 'NAD+ supplementation' },
  { id: 'none', label: 'None of the above' },
];

const CHALLENGE_OPTIONS = [
  { id: 'appetite', label: 'Controlling appetite and cravings' },
  { id: 'fatigue', label: 'Low energy and fatigue' },
  { id: 'sleep', label: 'Poor sleep and recovery' },
  { id: 'body-comp', label: 'Losing fat while keeping muscle' },
  { id: 'focus', label: 'Mental clarity and focus' },
  { id: 'hormones', label: 'Hormone-related symptoms' },
  { id: 'plateau', label: "I've tried things before and they stopped working" },
  { id: 'cost', label: 'Finding something I can afford' },
];

const ForecastPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedGoal = searchParams.get('goal');

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    goals: preselectedGoal ? [preselectedGoal] : [],
    currentWeight: '',
    goalWeight: '',
    heightFeet: '',
    heightInches: '',
    age: '',
    energy: '',
    sleep: '',
    activity: '',
    experience: [],
    challenge: '',
  });

  const showGoalWeight = answers.goals.includes('weight-loss');

  const steps = [
    { id: 'goals', title: 'What are you most focused on right now?', subtitle: 'Pick up to 3' },
    { id: 'currentWeight', title: 'What is your current weight?', subtitle: 'In pounds' },
    ...(showGoalWeight ? [{ id: 'goalWeight', title: 'What is your goal weight?', subtitle: 'In pounds' }] : []),
    { id: 'height', title: 'What is your height?', subtitle: '' },
    { id: 'age', title: 'How old are you?', subtitle: '' },
    { id: 'energy', title: 'How would you rate your current energy levels?', subtitle: '' },
    { id: 'sleep', title: 'How is your sleep?', subtitle: '' },
    { id: 'activity', title: 'How active are you currently?', subtitle: '' },
    { id: 'experience', title: 'Have you tried any of these before?', subtitle: 'Select all that apply' },
    { id: 'challenge', title: "What's your biggest challenge right now?", subtitle: '' },
  ];

  const totalSteps = steps.length;
  const currentStep = steps[step];
  const progress = ((step + 1) / totalSteps) * 100;

  const toggleGoal = (id) => {
    setAnswers(prev => {
      const current = prev.goals;
      if (id === 'none') return { ...prev, goals: [] };
      if (current.includes(id)) return { ...prev, goals: current.filter(g => g !== id) };
      if (current.length >= 3) return prev;
      return { ...prev, goals: [...current, id] };
    });
  };

  const toggleExperience = (id) => {
    setAnswers(prev => {
      const current = prev.experience;
      if (id === 'none') return { ...prev, experience: ['none'] };
      const filtered = current.filter(e => e !== 'none');
      if (filtered.includes(id)) return { ...prev, experience: filtered.filter(e => e !== id) };
      return { ...prev, experience: [...filtered, id] };
    });
  };

  const canProceed = () => {
    if (!currentStep) return false;
    switch (currentStep.id) {
      case 'goals': return answers.goals.length > 0;
      case 'currentWeight': return answers.currentWeight && Number(answers.currentWeight) > 50;
      case 'goalWeight': return answers.goalWeight && Number(answers.goalWeight) > 50;
      case 'height': return answers.heightFeet && answers.heightInches !== '';
      case 'age': return answers.age && Number(answers.age) > 10;
      case 'energy': return !!answers.energy;
      case 'sleep': return !!answers.sleep;
      case 'activity': return !!answers.activity;
      case 'experience': return answers.experience.length > 0;
      case 'challenge': return !!answers.challenge;
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      const params = new URLSearchParams();
      params.set('data', btoa(JSON.stringify(answers)));
      navigate(`/forecast/results?${params.toString()}`);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const renderStepContent = () => {
    if (!currentStep) return null;
    switch (currentStep.id) {
      case 'goals':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {GOALS.map(goal => (
              <button
                key={goal.id}
                onClick={() => toggleGoal(goal.id)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  answers.goals.includes(goal.id)
                    ? 'border-cyan-500 bg-cyan-500/10 text-white'
                    : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-white/20'
                }`}
                data-testid={`goal-${goal.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{goal.label}</span>
                  {answers.goals.includes(goal.id) && <Check className="h-5 w-5 text-cyan-400" />}
                </div>
              </button>
            ))}
          </div>
        );

      case 'currentWeight':
        return (
          <div className="max-w-xs mx-auto">
            <div className="relative">
              <input
                type="number"
                value={answers.currentWeight}
                onChange={e => setAnswers(prev => ({ ...prev, currentWeight: e.target.value }))}
                placeholder="220"
                className="w-full text-center text-4xl font-bold bg-slate-950/50 border border-white/10 rounded-xl p-6 text-white focus:border-cyan-500 focus:outline-none"
                data-testid="input-current-weight"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-xl">lbs</span>
            </div>
          </div>
        );

      case 'goalWeight':
        return (
          <div className="max-w-xs mx-auto">
            <div className="relative">
              <input
                type="number"
                value={answers.goalWeight}
                onChange={e => setAnswers(prev => ({ ...prev, goalWeight: e.target.value }))}
                placeholder="180"
                className="w-full text-center text-4xl font-bold bg-slate-950/50 border border-white/10 rounded-xl p-6 text-white focus:border-cyan-500 focus:outline-none"
                data-testid="input-goal-weight"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-xl">lbs</span>
            </div>
          </div>
        );

      case 'height':
        return (
          <div className="flex gap-4 justify-center max-w-xs mx-auto">
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-2 text-center">Feet</label>
              <input
                type="number"
                value={answers.heightFeet}
                onChange={e => setAnswers(prev => ({ ...prev, heightFeet: e.target.value }))}
                placeholder="5"
                min="3" max="8"
                className="w-full text-center text-3xl font-bold bg-slate-950/50 border border-white/10 rounded-xl p-4 text-white focus:border-cyan-500 focus:outline-none"
                data-testid="input-height-feet"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-slate-400 mb-2 text-center">Inches</label>
              <input
                type="number"
                value={answers.heightInches}
                onChange={e => setAnswers(prev => ({ ...prev, heightInches: e.target.value }))}
                placeholder="10"
                min="0" max="11"
                className="w-full text-center text-3xl font-bold bg-slate-950/50 border border-white/10 rounded-xl p-4 text-white focus:border-cyan-500 focus:outline-none"
                data-testid="input-height-inches"
              />
            </div>
          </div>
        );

      case 'age':
        return (
          <div className="max-w-xs mx-auto">
            <input
              type="number"
              value={answers.age}
              onChange={e => setAnswers(prev => ({ ...prev, age: e.target.value }))}
              placeholder="45"
              className="w-full text-center text-4xl font-bold bg-slate-950/50 border border-white/10 rounded-xl p-6 text-white focus:border-cyan-500 focus:outline-none"
              data-testid="input-age"
            />
          </div>
        );

      case 'energy':
        return (
          <div className="space-y-3 max-w-lg mx-auto">
            {ENERGY_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => setAnswers(prev => ({ ...prev, energy: opt.id }))}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  answers.energy === opt.id
                    ? 'border-cyan-500 bg-cyan-500/10 text-white'
                    : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-white/20'
                }`}
                data-testid={`energy-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        );

      case 'sleep':
        return (
          <div className="space-y-3 max-w-lg mx-auto">
            {SLEEP_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => setAnswers(prev => ({ ...prev, sleep: opt.id }))}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  answers.sleep === opt.id
                    ? 'border-cyan-500 bg-cyan-500/10 text-white'
                    : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-white/20'
                }`}
                data-testid={`sleep-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        );

      case 'activity':
        return (
          <div className="space-y-3 max-w-lg mx-auto">
            {ACTIVITY_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => setAnswers(prev => ({ ...prev, activity: opt.id }))}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  answers.activity === opt.id
                    ? 'border-cyan-500 bg-cyan-500/10 text-white'
                    : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-white/20'
                }`}
                data-testid={`activity-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-3 max-w-lg mx-auto">
            {EXPERIENCE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => toggleExperience(opt.id)}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  answers.experience.includes(opt.id)
                    ? 'border-cyan-500 bg-cyan-500/10 text-white'
                    : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-white/20'
                }`}
                data-testid={`exp-${opt.id}`}
              >
                <div className="flex items-center justify-between">
                  <span>{opt.label}</span>
                  {answers.experience.includes(opt.id) && <Check className="h-5 w-5 text-cyan-400" />}
                </div>
              </button>
            ))}
          </div>
        );

      case 'challenge':
        return (
          <div className="space-y-3 max-w-lg mx-auto">
            {CHALLENGE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => setAnswers(prev => ({ ...prev, challenge: opt.id }))}
                className={`w-full p-4 rounded-xl border text-left transition-all ${
                  answers.challenge === opt.id
                    ? 'border-cyan-500 bg-cyan-500/10 text-white'
                    : 'border-white/10 bg-slate-950/40 text-slate-300 hover:border-white/20'
                }`}
                data-testid={`challenge-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      <Helmet>
        <title>AI Health Optimization Forecast | MyWeightForecast</title>
        <meta name="description" content="Build your personalized 90-day health optimization forecast based on your goals, profile, and lifestyle." />
      </Helmet>

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-slate-800">
        <motion.div
          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-16 pb-24">
        {/* Step Counter */}
        <div className="text-center mb-2">
          <span className="text-sm text-slate-500">Step {step + 1} of {totalSteps}</span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
          >
            {/* Question */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2" data-testid="forecast-question">
                {currentStep?.title}
              </h1>
              {currentStep?.subtitle && (
                <p className="text-slate-400">{currentStep.subtitle}</p>
              )}
            </div>

            {/* Step Content */}
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-10">
          <button
            onClick={handleBack}
            className={`flex items-center gap-2 text-slate-400 hover:text-white transition-colors ${step === 0 ? 'invisible' : ''}`}
            data-testid="forecast-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-white rounded-full px-8 py-3 font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="forecast-next-btn"
          >
            {step === totalSteps - 1 ? 'See My Forecast' : 'Continue'}
            {step < totalSteps - 1 && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ForecastPage;
